import { Language, ReportOrientationType, ReportSizeType } from '@/core';
import { enumValues } from '@/utils';
import type { PersonalizationSummaryFormValue } from './DownloadPersonalizationSummaryPdfModal';

export type PersonalizationSummaryPdfSettings = Pick<
  PersonalizationSummaryFormValue,
  | 'size'
  | 'orientation'
  | 'language'
  | 'showItemDescription'
  | 'showConstructionDescription'
  | 'showSignature'
  | 'showConstruction'
  | 'showResidentInfo'
  | 'showBaseCost'
  | 'showResidentPrice'
  | 'showFloorplanImage'
  | 'showInactiveItems'
>;

const STORAGE_KEY_PREFIX = 'personalization-summary-pdf-settings';
const SIZE_VALUES = enumValues(ReportSizeType);
const ORIENTATION_VALUES = enumValues(ReportOrientationType);
const LANGUAGE_VALUES = enumValues(Language);
const BOOLEAN_SETTING_KEYS = [
  'showItemDescription',
  'showConstructionDescription',
  'showSignature',
  'showConstruction',
  'showResidentInfo',
  'showBaseCost',
  'showResidentPrice',
  'showFloorplanImage',
  'showInactiveItems',
] as const;

type BooleanSettingKey = typeof BOOLEAN_SETTING_KEYS[number];

const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}:${userId}`;

function isEnumValue<T extends string>(
  value: unknown,
  values: T[],
): value is T {
  return typeof value === 'string' && values.includes(value as T);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function parseSettings(
  value: unknown,
): Partial<PersonalizationSummaryPdfSettings> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const stored = value as Record<string, unknown>;
  const settings: Partial<PersonalizationSummaryPdfSettings> = {};

  if (isEnumValue(stored.size, SIZE_VALUES)) settings.size = stored.size;
  if (isEnumValue(stored.orientation, ORIENTATION_VALUES)) {
    settings.orientation = stored.orientation;
  }
  if (isEnumValue(stored.language, LANGUAGE_VALUES)) {
    settings.language = stored.language;
  }

  BOOLEAN_SETTING_KEYS.forEach((field: BooleanSettingKey) => {
    if (isBoolean(stored[field])) {
      settings[field] = stored[field] as boolean;
    }
  });

  return settings;
}

function getSettingsFromValues(
  values: PersonalizationSummaryFormValue,
): PersonalizationSummaryPdfSettings {
  return {
    size: values.size,
    orientation: values.orientation,
    language: values.language,
    showItemDescription: values.showItemDescription,
    showConstructionDescription: values.showConstructionDescription,
    showSignature: values.showSignature,
    showConstruction: values.showConstruction,
    showResidentInfo: values.showResidentInfo,
    showBaseCost: values.showBaseCost,
    showResidentPrice: values.showResidentPrice,
    showFloorplanImage: values.showFloorplanImage,
    showInactiveItems: values.showInactiveItems,
  };
}

export function getPersonalizationSummaryPdfSettings(
  userId?: string,
): Partial<PersonalizationSummaryPdfSettings> {
  if (!userId) {
    return {};
  }

  try {
    const stored = JSON.parse(
      window.localStorage.getItem(getStorageKey(userId)) ?? 'null',
    );

    return parseSettings(stored);
  } catch {
    return {};
  }
}

export function savePersonalizationSummaryPdfSettings(
  userId: string | undefined,
  values: PersonalizationSummaryFormValue,
) {
  if (!userId) {
    return;
  }

  const settings = getSettingsFromValues(values);

  try {
    window.localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify(settings),
    );
  } catch (error) {
    console.error(
      'Failed to save personalization summary PDF settings to localStorage.',
      error,
    );
  }
}
